// sdv-site-header.js
// ════════════════════════════════════════════════════════════════════════
// Comportements interactifs du header, footer, cart drawer, FAQ accordion,
// event carousel et bandeau cookies. Le HTML est désormais émis par Liquid
// (sections sdv-header, sdv-footer, sdv-cart-drawer) — ce fichier n'injecte
// plus de markup.
// ════════════════════════════════════════════════════════════════════════

(() => {

  // Conserve le préfixe de langue sur les liens internes qui proviennent
  // des réglages de sections Shopify (ces URL sont stockées sans /en ou /es).
  const localizeInternalLinks = (scope = document) => {
    const locale = (document.documentElement.lang || "").toLowerCase().split("-")[0];
    if (locale !== "en" && locale !== "es") return;
    const prefix = `/${locale}`;
    scope.querySelectorAll?.('a[href^="/"]').forEach((link) => {
      const href = link.getAttribute("href");
      if (!href || href.startsWith("//") || href === prefix || href.startsWith(`${prefix}/`)) return;
      if (/^\/(?:apps|api|admin|customer_authentication)(?:\/|$)/.test(href)) return;
      link.setAttribute("href", `${prefix}${href}`);
    });
  };

  localizeInternalLinks();
  document.addEventListener("DOMContentLoaded", () => localizeInternalLinks());
  document.addEventListener("shopify:section:load", (event) => localizeInternalLinks(event.target));

  // ── Header — mobile toggle + dropdowns hover desktop ────────────────
  document.querySelectorAll("[data-site-header]").forEach((header) => {
    const toggle = header.querySelector(".site-header__toggle");
    const mobileMenu = header.querySelector("[data-mobile-menu]");
    const subs = mobileMenu ? Array.from(mobileMenu.querySelectorAll("[data-mobile-sub]")) : [];
    const resetSubs = () => subs.forEach((s) => s.classList.remove("is-active"));

    const closeMenu = () => {
      header.classList.remove("is-open");
      toggle?.setAttribute("aria-expanded", "false");
      document.body.classList.remove("mobile-menu-open");
      resetSubs();
    };

    toggle?.addEventListener("click", () => {
      const isOpen = header.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", String(isOpen));
      document.body.classList.toggle("mobile-menu-open", isOpen);
      if (!isOpen) resetSubs();
    });

    mobileMenu?.querySelectorAll("[data-mobile-close]").forEach((btn) => {
      btn.addEventListener("click", closeMenu);
    });

    // Menu mobile drill-down : ouvrir un sous-écran / revenir en arrière
    mobileMenu?.querySelectorAll("[data-mobile-open]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.getAttribute("data-mobile-open");
        subs.forEach((s) => s.classList.toggle("is-active", s.getAttribute("data-mobile-sub") === id));
      });
    });
    mobileMenu?.querySelectorAll("[data-mobile-back]").forEach((btn) => {
      btn.addEventListener("click", resetSubs);
    });

    const desktopNav = header.querySelector(".site-nav");
    if (desktopNav && window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      const groups = Array.from(desktopNav.querySelectorAll(".site-nav__group"));
      let fermerLater = null;
      const fermerTous = (sauf) => groups.forEach((g) => { if (g !== sauf) g.removeAttribute("open"); });

      groups.forEach((g) => {
        const summary = g.querySelector("summary");
        g.addEventListener("mouseenter", () => {
          if (fermerLater) { clearTimeout(fermerLater); fermerLater = null; }
          fermerTous(g);
          g.setAttribute("open", "");
        });
        g.addEventListener("mouseleave", () => {
          if (fermerLater) clearTimeout(fermerLater);
          fermerLater = setTimeout(() => g.removeAttribute("open"), 140);
        });
        summary?.addEventListener("click", (e) => {
          if (g.hasAttribute("open")) e.preventDefault();
        });
      });

      desktopNav.addEventListener("mouseleave", () => {
        if (fermerLater) clearTimeout(fermerLater);
        fermerLater = setTimeout(() => fermerTous(null), 140);
      });
    }
  });

  // ── Header rétractable : se compacte au scroll ──────────────────────
  // Ajoute/retire .is-header-compact sur <html> selon la position de
  // scroll (avec hystérésis pour éviter toute oscillation). La CSS anime
  // alors la SEULE hauteur du header (--header-tall → 64px, desktop) ;
  // l'offset des héros (--home-header-height) reste constant → aucun
  // recalcul 100vh, aucun clignotement (cf. sdv-styles.css).
  (function () {
    const root = document.documentElement;
    const COMPACT_AT = 80; // se compacte une fois passé 80px
    const EXPAND_AT = 30; // ne se ré-étend qu'en repassant sous 30px
    let compact = false;
    let ticking = false;
    function maj() {
      const y = window.scrollY;
      if (!compact && y > COMPACT_AT) {
        compact = true;
        root.classList.add("is-header-compact");
      } else if (compact && y < EXPAND_AT) {
        compact = false;
        root.classList.remove("is-header-compact");
      }
      ticking = false;
    }
    window.addEventListener(
      "scroll",
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(maj);
        }
      },
      { passive: true }
    );
    maj();
  })();

  // ── Footer — colonnes accordéon mobile, ouvert desktop ──────────────
  document.querySelectorAll("[data-site-footer]").forEach((footer) => {
    const mobileFooterQuery = window.matchMedia("(max-width: 540px)");
    const columns = Array.from(footer.querySelectorAll(".site-footer__column"));
    const sync = () => columns.forEach((col) => {
      if (mobileFooterQuery.matches) col.removeAttribute("open");
      else col.setAttribute("open", "");
    });
    sync();
    mobileFooterQuery.addEventListener?.("change", sync);
  });

  // ── Cart drawer — ouverture/fermeture ────────────────────────────────
  // Le contenu (items, total) est géré par sdv-cart.js (Shopify Ajax Cart, Sprint 2).
  const drawer = document.querySelector("[data-cart-drawer]");

  function ouvrirCart() {
    if (!drawer) return;
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
  }
  function fermerCart() {
    if (!drawer) return;
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
  }

  document.querySelectorAll("[data-cart-open]").forEach((trigger) => {
    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      window.SDVCart?.refresh?.();
      ouvrirCart();
    });
  });
  document.querySelectorAll("[data-cart-close]").forEach((trigger) => {
    trigger.addEventListener("click", fermerCart);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") fermerCart();
  });

  document.body.addEventListener("click", (e) => {
    if (e.target.closest && e.target.closest(".cart-toast")) ouvrirCart();
  });

  window.SDVDrawer = { ouvrir: ouvrirCart, fermer: fermerCart };

  // ── FAQ accordion : un seul ouvert à la fois ─────────────────────────
  [
    ".contact-faq details",
    ".product-accordion details",
  ].forEach((selector) => {
    document.querySelectorAll(selector).forEach((detail) => {
      detail.addEventListener("toggle", () => {
        if (!detail.open) return;
        document.querySelectorAll(selector).forEach((other) => {
          if (other !== detail) other.removeAttribute("open");
        });
      });
    });
  });

  // ── Event carousel (rendez-vous home / process page vendeur) ─────────
  const eventCards = Array.from(document.querySelectorAll(".event-carousel .event-card"));
  if (eventCards.length) {
    const eventCount = document.querySelector("[data-event-count]");
    const eventPrev  = document.querySelector("[data-event-prev]");
    const eventNext  = document.querySelector("[data-event-next]");
    const eventViewport = document.querySelector(".event-carousel__viewport");
    let activeEvent = 0;
    let swipeStartX = 0;
    let swipeStartY = 0;

    function showEvent(index) {
      activeEvent = (index + eventCards.length) % eventCards.length;
      eventCards.forEach((card, i) => card.classList.toggle("is-active", i === activeEvent));
      if (eventCount) {
        eventCount.textContent =
          `${String(activeEvent + 1).padStart(2, "0")} / ${String(eventCards.length).padStart(2, "0")}`;
      }
    }

    eventPrev?.addEventListener("click", () => showEvent(activeEvent - 1));
    eventNext?.addEventListener("click", () => showEvent(activeEvent + 1));

    eventViewport?.addEventListener("touchstart", (e) => {
      const t = e.touches[0]; swipeStartX = t.clientX; swipeStartY = t.clientY;
    }, { passive: true });
    eventViewport?.addEventListener("touchend", (e) => {
      const t = e.changedTouches[0];
      const dx = t.clientX - swipeStartX;
      const dy = t.clientY - swipeStartY;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
      showEvent(dx < 0 ? activeEvent + 1 : activeEvent - 1);
    }, { passive: true });
  }

  // ── Bandeau consentement cookies ─────────────────────────────────────
  (function () {
    const KEY = "sdv_cookie_consent";
    if (localStorage.getItem(KEY)) return;

    const locale = (document.documentElement.lang || "fr").toLowerCase().split("-")[0];
    const cookieTranslations = {
      en: {
        label: "Cookie settings",
        text: "We use cookies to improve your experience and measure site traffic.",
        more: "Learn more",
        accept: "Accept all",
        refuse: "Reject"
      },
      es: {
        label: "Configuración de cookies",
        text: "Utilizamos cookies para mejorar tu experiencia y medir el tráfico del sitio.",
        more: "Más información",
        accept: "Aceptar todo",
        refuse: "Rechazar"
      },
      fr: {
        label: "Gestion des cookies",
        text: "Nous utilisons des cookies pour améliorer votre expérience et mesurer l'audience du site.",
        more: "En savoir plus",
        accept: "Accepter tout",
        refuse: "Refuser"
      }
    };
    const copy = cookieTranslations[locale] || cookieTranslations.fr;

    const banner = document.createElement("div");
    banner.id = "cookie-banner";
    banner.setAttribute("role", "dialog");
    banner.setAttribute("aria-label", copy.label);
    banner.innerHTML = `
      <p class="cookie-banner__text">
        ${copy.text}
        <a href="${((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/?$/, '/')}pages/politique-cookies" class="cookie-banner__link">${copy.more}</a>
      </p>
      <div class="cookie-banner__actions">
        <button id="cookie-accept" class="cookie-banner__btn cookie-banner__btn--accept">${copy.accept}</button>
        <button id="cookie-refuse" class="cookie-banner__btn cookie-banner__btn--refuse">${copy.refuse}</button>
      </div>
    `;

    const style = document.createElement("style");
    style.textContent = `
      #cookie-banner {
        position: fixed; bottom: 0; left: 0; right: 0; z-index: 9999;
        background: var(--noir); color: #fff;
        display: flex; align-items: center; justify-content: space-between;
        flex-wrap: wrap; gap: 16px; padding: 18px 32px;
        font-size: 13px; line-height: 1.6;
        transform: translateY(0);
        transition: transform 320ms cubic-bezier(0.22, 0.61, 0.36, 1), opacity 280ms ease;
      }
      #cookie-banner.is-hiding { transform: translateY(100%); opacity: 0; pointer-events: none; }
      .cookie-banner__text { flex: 1; min-width: 260px; color: rgba(255,255,255,0.8); }
      .cookie-banner__link { color: var(--or); text-decoration: underline; text-underline-offset: 3px; }
      .cookie-banner__link:hover { color: #fff; }
      .cookie-banner__actions { display: flex; gap: 10px; flex-shrink: 0; }
      .cookie-banner__btn {
        padding: 9px 20px; font-size: 11px; font-weight: 800;
        letter-spacing: 0.1em; text-transform: uppercase; border: none;
        cursor: pointer; transition: opacity 140ms;
      }
      .cookie-banner__btn:hover { opacity: 0.85; }
      .cookie-banner__btn--accept { background: var(--or); color: var(--noir); }
      .cookie-banner__btn--refuse { background: transparent; color: rgba(255,255,255,0.6); border: 1px solid rgba(255,255,255,0.25); }
      .cookie-banner__btn--refuse:hover { color: #fff; border-color: rgba(255,255,255,0.6); opacity: 1; }
      @media (max-width: 600px) {
        #cookie-banner { padding: 16px 20px; }
        .cookie-banner__actions { width: 100%; }
        .cookie-banner__btn { flex: 1; text-align: center; }
      }
    `;

    function dismiss(choice) {
      localStorage.setItem(KEY, choice);
      banner.classList.add("is-hiding");
      setTimeout(() => banner.remove(), 400);
    }

    document.head.appendChild(style);
    document.body.appendChild(banner);

    document.getElementById("cookie-accept").addEventListener("click", () => dismiss("accepted"));
    document.getElementById("cookie-refuse").addEventListener("click", () => dismiss("refused"));
  })();

})();
